import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';

import { Popover } from '../../../global/popover/popover';
import DefaultButton from '../../../global/default-button/default-button';
import './event-tracker-saved-filters.scss';
import { IFiltersView, ISelectedFilter } from '../../../../api/events.dao';
import Text from '../../../global/text/text';
import { SaveFilteredViewModal } from './_save-filtered-view/save-filtered-view-modal';
import SavedItem from '../../../global/_saved-item/_saved-item';
import { LOADING } from '../../../../constants/common-labels.constants';


export interface IProps {
    intl: IntlShape;
    selectedFilters: ISelectedFilter[];
    filterViews: IFiltersView[];
    onClearFilters: () => void;
    onDeleteView: (id: number) => void;
    onSelectViewFilters: (filters: ISelectedFilter[]) => void;
    fetchItems: () => Promise<void>;
    onSave: (view: IFiltersView) => Promise<boolean>;
    columnsCount: number;
}

const SavedFilters = injectIntl((props: IProps) => {
    const {
        intl,
        selectedFilters,
        filterViews,
        onClearFilters,
        onDeleteView,
        fetchItems,
        onSelectViewFilters,
        onSave,
        columnsCount
    } = props;

    const [isAddingNewModalOpen, setIsAddingNewModalOpen] = useState(false);
    const [isViewsOpen, setIsViewsOpen] = useState(false);

    const viewsRef = useRef();

    const isFiltersSelected = (): boolean => {
        return !selectedFilters.length || selectedFilters.some(el => {
            if (el.filterName === 'Columns') {
                return el.filterValues.length !== columnsCount;
            } else {
                return !!selectedFilters.length;
            }
        });
     };

    const handleToggleViews = () => {
        setIsViewsOpen(prev => !prev);
    };

    const handleOpenNewViewModal = () => {
        setIsAddingNewModalOpen(true);
    };

    const handleCloseNewViewModal = () => {
        setIsAddingNewModalOpen(false);
    };

    const handleSelectClick = (filters: ISelectedFilter[]) => {
        setIsViewsOpen(false);
        onSelectViewFilters(filters);
    };

    const handleSaveNewView = async (view: IFiltersView) => {
        if (await onSave(view)) handleCloseNewViewModal();
    };

    const selectedView = useMemo(() => selectedFilters.length ? filterViews?.find(view => {
        if (view.filters.length === selectedFilters.length) {
            return view.filters.reduce((isEquals, viewFilter) => {
                if (!isEquals) return false;

                const selectedFilter = selectedFilters.find(el => el.filterName === viewFilter.filterName);
                const selectedFilterValues = selectedFilter?.filterValues || [];

                if (selectedFilterValues.length === viewFilter.filterValues.length) {
                    const selectedFilterValueIds = selectedFilterValues.map(el => el.id);
                    return viewFilter.filterValues.map(el => el.id).every(el => selectedFilterValueIds.includes(el));
                }
            }, true);
        }
    }) : null, [selectedFilters, filterViews]);

    useEffect(() => {
        fetchItems();
    }, []);

    useEffect(() => {
        if (isViewsOpen) fetchItems();
    }, [isViewsOpen]);

    return (
        <div className='saved-filters'>
            { isFiltersSelected() && <DefaultButton
                modifier='link'
                text={intl.formatMessage(
                    {
                        id: 'oe.eventTracker.clearAllFilters',
                        description: 'Clear filters button',
                        defaultMessage: 'Clear All Filters'
                    }
                )}
                onClick={onClearFilters}
            />}
            <div
                className={classnames('saved-filters__view-button', {
                    'saved-filters__view-button--selected': isViewsOpen
                })}
                onClick={handleToggleViews}
                ref={viewsRef}
            >
                <>
                    <div className='saved-filters__view-button-text' title={selectedView?.filterViewName}>
                        <div>
                            {intl.formatMessage(
                                {
                                    id: 'oe.eventTracker.myViews',
                                    description: 'My views button',
                                    defaultMessage: 'My Views',
                                }
                            )}
                        </div>
                        { selectedView && <Text tag='span' message={`: ${selectedView.filterViewName}`} withEllipsis withoutTitle /> }
                    </div>
                    <Popover
                        isOpen={isViewsOpen}
                        buttonRef={viewsRef}
                        onClickOutside={() => setIsViewsOpen(false)}
                        customWidth={300}
                        padding={0}
                        align='end'
                        position='bottom'
                    >
                        <div className='w-100 saved-filters__container' onClick={e => e.stopPropagation()}>
                            <div
                                onClick={() => isFiltersSelected() && handleOpenNewViewModal()}
                                className={classnames('saved-filters__save-button', {
                                'saved-filters__save-button--disabled': !isFiltersSelected()
                            })}>
                                <div className='saved-filters__save-button-label'>
                                    { intl.formatMessage(
                                        {
                                            id: 'oe.eventTracker.saveFilteredView',
                                            description: 'Save filtered view button',
                                            defaultMessage: 'Save Filtered View',
                                        }
                                    )}
                                </div>
                                { !isFiltersSelected() && <div className='saved-filters__save-button-hint'>
                                    { intl.formatMessage(
                                        {
                                            id: 'oe.eventTracker.noFiltersAreSelected',
                                            description: 'Text label',
                                            defaultMessage: '* No filters are selected',
                                        }
                                    )}
                                </div> }
                            </div>
                            { filterViews ? (
                                <div className='saved-filters__content'>
                                    { filterViews.map(view => (
                                        <SavedItem
                                            key={view.userFilterViewId}
                                            name={view.filterViewName}
                                            onSelect={() => handleSelectClick(view.filters)}
                                            onDelete={() => onDeleteView(view.userFilterViewId)}
                                            isSelected={selectedView?.userFilterViewId === view.userFilterViewId}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className='saved-filters__placeholder'>
                                    <FormattedMessage
                                        {...LOADING}
                                        values={{
                                            suffix: '...'
                                        }}
                                    />
                            </div>
                            )}
                        </div>
                    </Popover>
                </>
            </div>
            { isAddingNewModalOpen &&
                <SaveFilteredViewModal
                    selectedFilters={selectedFilters}
                    onSave={handleSaveNewView}
                    onClose={handleCloseNewViewModal}
                />
            }
        </div>);
});

export default SavedFilters;
